/**
 * DashboardHeader.jsx — v3.7.15 Phase 84.32 Part 1
 *
 * Compact top-of-Overview header. Replaces the two large scroll sections
 * (ActivationChecklist + RecordManualTxnCard) with two right-aligned
 * controls next to the greeting:
 *
 *   • GettingStartedPill  — shows "4 of 5" progress, opens a right-side
 *                           drawer with the full checklist. Auto-hides
 *                           after 5/5 has been acknowledged (per spec).
 *   • RecordTransactionButton — opens the new Record Transaction modal.
 *
 * The greeting + subtitle on the left is unchanged from the legacy
 * behaviour; we just move it into a real semantic <header> and hoist
 * the two new controls into the same row.
 */
import React from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GettingStartedPill from '@/components/dashboard/GettingStartedPill';

export default function DashboardHeader({
  greetingName,
  subtitle,
  activation,          // { hasAccount, hasImported, hasCategorised, hasGoal, hasReflection }
  onOpenChecklist,
  onOpenRecord,
}) {
  const greetingHour = new Date().getHours();
  const greetingWord =
    greetingHour < 5  ? 'Good evening' :
    greetingHour < 12 ? 'Good morning' :
    greetingHour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <header
      className="flex items-start justify-between gap-4 flex-wrap"
      data-testid="dashboard-header"
    >
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground/80">{greetingWord},</div>
        <div className="font-display text-xl sm:text-2xl font-medium text-charcoal truncate">
          {greetingName || 'there'}
        </div>
        {subtitle && (
          <div className="text-xs text-muted-foreground mt-1 max-w-md">
            {subtitle}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <GettingStartedPill
          activation={activation}
          onOpen={onOpenChecklist}
        />
        <Button
          type="button"
          onClick={onOpenRecord}
          className="rounded-full h-9 px-4"
          data-testid="dashboard-record-txn-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" aria-hidden="true" />
          <span className="hidden sm:inline">Record transaction</span>
          <span className="sm:hidden sr-only">Record transaction</span>
        </Button>
      </div>
    </header>
  );
}
